import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormik } from 'formik';

import {
  useGetMyDetails,
  useUpdateUser,
} from '../../../services/redux/api/usersApi';
import { FormMode } from '../../../shared/constants';
import { getUserInfoFormValidation } from './FormikData';
import { useLayout } from '../../../context/LayoutContext';
import { useGetFilePaths } from '../../../services/redux/api/appConfigApi';
import {
  deleteFilesFromFirebase,
  uploadFileAndCreateAppFile,
} from '../../../utils/firebase-storage-utils';

export function useUserInfoComponent() {
  const { data: user } = useGetMyDetails();
  const [updateUser] = useUpdateUser();
  const { t } = useTranslation();
  const [avatar, setAvatar] = useState(null);
  const [mode, setMode] = useState<FormMode>(FormMode.view);
  const { data: filePaths } = useGetFilePaths();
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: getUserInfoFormValidation(t),
    initialValues: {
      firstname: user.firstname,
      lastname: user.lastname,
      description: user.description,
      avatar: '',
    },
    onSubmit,
  });

  function onCancelChanges() {
    formik.resetForm({
      values: {
        firstname: user.firstname,
        lastname: user.lastname,
        description: user.description,
        avatar: '',
      },
    });
    setAvatar(null);
    setMode(FormMode.view);
  }

  async function onSubmit() {
    if (!filePaths || !user) {
      return;
    }

    let avatarFile;
    const uploadedStoragePaths: string[] = [];

    try {
      setIsLoading(true);

      if (avatar) {
        avatarFile = await uploadFileAndCreateAppFile(avatar, filePaths.users);

        uploadedStoragePaths.push(avatarFile.storageLink);
        if (avatarFile.previewStorageLink) {
          uploadedStoragePaths.push(avatarFile.previewStorageLink);
        }
      }

      await updateUser({
        id: user.id,
        body: {
          ...formik.values,
          ...(avatarFile && { avatar: avatarFile }),
        } as any,
      }).unwrap();

      setAvatar(null);
      setMode(FormMode.view);
    } catch (error) {
      console.error('Error uploading avatar or saving user:', error);
      if (uploadedStoragePaths.length > 0) {
        await deleteFilesFromFirebase(uploadedStoragePaths);
      }
    } finally {
      setIsLoading(false);
    }
  }

  return {
    user,
    formik,
    onCancelChanges,
    avatar,
    setAvatar,
    isLoading,
    mode,
    setMode,
  };
}
